import _ from 'lodash'
import { NetworkInvitation } from '@/core/models/network.model'
import { Event } from '@/core/models/notification.model'
import { SearchResponse } from '@/core/types/response'
import { SearchRequestOptions } from '@/core/types/request'
import { SocketEvent } from '@/core/types/socket-event'

import { RootState } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { cacher } from '@/utils/rtkQueryCacheUtils'

import { Tag, baseApi } from './base'
import { getSocket } from './socket'
import { AuthUser, UpdateAuthUserDto } from '../classes/Auth'

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMagicLink: builder.query<{ url: string }, string>({
      query: (redirectUri) => ({
        url: '/auth/magic-link',
        method: 'GET',
        params: {
          redirect_uri: redirectUri,
        },
      }),
    }),
    getMyNotifications: builder.query<SearchResponse<Event>, Pick<SearchRequestOptions, 'limit' | 'skip'> & { cacheKey: string }>({
      query: ({ limit = 5, skip = 0 }) => ({
        url: '/auth/notifications',
        method: 'GET',
        params: { limit, skip },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}(${queryArgs.cacheKey})`,
      merge: (currentCache, newResponse) => {
        currentCache.items = _.uniqBy([...currentCache.items, ...newResponse.items], '_id')
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg !== previousArg
      },
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        const socket = await getSocket()
        try {
          await cacheDataLoaded
          socket.on('notification', (event: Event) => {
            updateCachedData((cachedData) => {
              cachedData.items.unshift(event)
              cachedData.totalCount += 1
            })
          })
        } catch {}
        await cacheEntryRemoved
        socket.off('notification')
      },
    }),
    markNotificationsAsObserved: builder.mutation<Event[], { eventIds: string[]; state: 'read' | 'seen' }>({
      query: ({ eventIds, state }) => ({
        url: `/auth/notifications/${state}`,
        method: 'PATCH',
        body: { eventIds },
      }),
      async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: updatedEvents } = await queryFulfilled
          const getMyNotificationArgs = authApi.util.selectCachedArgsForQuery(getState(), 'getMyNotifications')[0]
          dispatch(
            authApi.util.updateQueryData('getMyNotifications', getMyNotificationArgs, ({ items: cachedEvents }) => {
              for (const cachedEvent of cachedEvents) {
                const updatedEvent = updatedEvents.find((event) => event._id === cachedEvent._id)
                if (!updatedEvent) continue
                Object.assign(cachedEvent, updatedEvent)
              }
            })
          )
        } catch {}
      },
    }),
    getMyNetworkInvitations: builder.query<NetworkInvitation[], { limit?: number; skip: number }>({
      query: ({ limit = 20, skip }) => ({
        url: '/auth/network-invitations',
        method: 'GET',
        params: { limit, skip },
      }),
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved, getState, updateCachedData }) {
        const socket = await getSocket()
        const authUser = selectAuthUser(getState() as RootState)
        const networkInvitationSentListener = (networkInvitation: NetworkInvitation) => {
          if (authUser?._id === networkInvitation.invitee) {
            updateCachedData((cachedNetworkInvitations) => {
              cachedNetworkInvitations.push(networkInvitation)
            })
          }
        }
        const networkInvitationDeletedListener = (deletedNetworkInvitation: NetworkInvitation) => {
          if (authUser?._id === deletedNetworkInvitation.invitee) {
            updateCachedData((cachedNetworkInvitations) => {
              return cachedNetworkInvitations.filter((networkInvitation) => networkInvitation._id !== deletedNetworkInvitation._id)
            })
          }
        }
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.NETWORK_INVITATION_SENT, networkInvitationSentListener)
          socket.on(SocketEvent.NETWORK_INVITATION_DELETED, networkInvitationDeletedListener)
        } catch {}
        await cacheEntryRemoved
        socket.off(SocketEvent.NETWORK_INVITATION_SENT, networkInvitationSentListener)
        socket.off(SocketEvent.NETWORK_INVITATION_DELETED, networkInvitationDeletedListener)
      },
      providesTags: cacher.providesList(Tag.NETWORK_INVITATION),
    }),
    modifyPassword: builder.mutation<void, string>({
      query: (newPass) => ({ url: `/auth/pass`, method: 'PATCH', body: { newPass } }),
    }),
    updateMyProfile: builder.mutation<AuthUser, UpdateAuthUserDto>({
      query: (body) => ({ url: '/auth/me', method: 'PATCH', body }),
    }),
  }),
  overrideExisting: false,
})
